import { Button, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import CustomItemStatus from "../../../../../../common/components/custom_item_status";
import dayjs from "dayjs";

/**
 * GenerateRequisicaoServicosProdutosColumnDefinition
 */

type Props = {
  onActionEditar: (idRequisicaoCompra: number | null, openNewTab: boolean) => void;
  onActionDelete: (idRequisicaoCompra: number | null, openState: boolean) => void;
  onActionExportar: (idRequisicaoCompra: number) => void;
  onActionEmail: (idRequisicaoCompra: number | null, openState: boolean) => void;
  onActionCopy: (idRequisicaoCompra: number) => void;
};

export const GenerateRequisicaoServicosProdutosColumnDefinition = ({
  onActionEditar,
  onActionDelete,
  onActionExportar,
  onActionEmail,
  onActionCopy,
}: Props): GridColDef[] => {

  const computeStatusTitle = (status: number) => {
    switch (status) {
      case 0:
        return "Pendente";
      case 1:
        return "Aprovado";
      case 2:
        return "Reprovado";
      case 3:
        return "Atrasada";
      case 4:
        return "Finalizada";
      case 5:
        return "Revisar";
      case 6:
        return "Coleta Iniciada";
      default:
        return "?";
    }
  };

  const computeStatusColor = (status: number) => {
    switch (status) {
      case 0:
        return "#c6c6c6";
      case 1:
        return "#45b6af";
      case 2:
        return "#f3565d";
      case 3:
        return "#f8cb00";
      case 4:
        return "#428bca";
      case 5:
        return "#89c4f4";
      case 6:
        return "#999999";
      default:
        return "#0303FF";
    }
  };

  const computeStatusDescription = (status: number) => {
    switch (status) {
      case 0:
        return "Pendente";
      case 1:
        return "Aprovado";
      case 2:
        return "Reprovado";
      case 3:
        return "Atrasada";
      case 4:
        return "Finalizada";
      case 5:
        return "Revisar";
      case 6:
        return "Coleta Iniciada";
      default:
        return "?";
    }
  };

  return [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <CustomItemStatus
            size="autowidth"
            textSize="small"
            title={computeStatusTitle(params.value)}
            bgColor={computeStatusColor(params.value)}
            description={computeStatusDescription(params.value)} />
        </div>
      )
    },
    {
      field: "gerouOrdemCompra",
      filterable: false,
      headerName: "Gerou OC?",
      width: 60,
      minWidth: 60,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center">
          {value === true ? 'Sim' : 'Não'}
        </div>
      )
    },
    {
      field: "nrRequisicaoCompra",
      filterable: false,
      headerName: "Nº",
      width: 75,
      minWidth: 75,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center">
          {value}
        </div>
      )
    },
    {
      field: "descricao",
      filterable: false,
      headerName: "Descrição",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center">
          {value}
        </div>
      )
    },
    {
      field: "dataRequisicao",
      filterable: false,
      headerName: "Data Requisição",
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex items-center">
            <span>{value && dayjs(value).format('DD/MM/YYYY')}</span>
          </div>
        );
      },
    },
    {
      field: "centroCusto",
      filterable: false,
      headerName: "Centro de Custo",
      width: 175,
      minWidth: 75,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center">
          {value}
        </div>
      )
    },
    {
      field: "dataPrevista",
      filterable: false,
      headerName: "Data Prevista",
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex items-center">
            <span>{value && dayjs(value).format('DD/MM/YYYY')}</span>
          </div>
        );
      },
    },
    {
      field: "funcionario",
      filterable: false,
      headerName: "Solicitante",
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex items-center">
          {value}
        </div>
      )
    },
    {
      field: "dataAprovacao",
      filterable: false,
      headerName: "Data Aprovação",
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex items-center">
            <span>{value && dayjs(value).format('DD/MM/YYYY')}</span>
          </div>
        );
      },
    },
    {
      field: 'idRequisicaoCompra',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 100,
      resizable: false,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton
              onMouseDown={e => {
                if (e.button === 1) {
                  onActionEditar(value, true);
                } else {
                  onActionEditar(value, e.ctrlKey);
                }
                e.preventDefault();
              }}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => { onActionDelete(value, true) }}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
    {
      field: "exportar",
      filterable: false,
      headerName: "Exportar",
      sortable: false,
      valueGetter: (_, row) => { return row['idRequisicaoCompra'] as number; },
      renderCell: ({ value }) => {
        return (
          <Button
            onMouseDown={() => { onActionExportar(value) }}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.2rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
            }}>
            Exportar
          </Button>
        );
      },
    },
    {
      field: 'email',
      sortable: false,
      filterable: false,
      headerName: 'Enviar Email',
      width: 65,
      valueGetter: (_, row) => row['idRequisicaoCompra'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => { onActionEmail(value, true) }}>
              <DynamicIcon nameIcon="EmailOutlined" size={25} className="text-[#009C4E]" />
            </IconButton>
          </div>
        );
      }
    },
    {
      field: 'copiar',
      sortable: false,
      filterable: false,
      headerName: 'Copiar',
      width: 65,
      valueGetter: (_, row) => row['idRequisicaoCompra'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => { onActionCopy(value) }}>
              <DynamicIcon nameIcon="ContentCopy" size={25} className="text-purple-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}